import React, { useState } from 'react';
import classnames from 'classnames';
import { SubmitHandler, useForm } from 'react-hook-form';
import { MentionsInput, Mention, SuggestionDataItem } from 'react-mentions';

import Button, { BUTTON_TYPES } from '@buyside/components/presentation/Button/button';
import ProfileImage, {
  IMAGE_SIZES
} from '@buyside/components/presentation/ProfileImage/profileImage';

const FeedItemCommentsQuery = require('buyside/graphql/queries/Comments');

import {
  CreatePostCommentMutationVariables,
  CommentsQuery,
  CommentsQueryVariables,
  Maybe,
  PostCommentsQuery,
  PostCommentsDocument,
  useCreatePostCommentMutation
} from '@interfaces/graphql-typings';

import { defaultUserImageUrl } from '@buyside/util/urlHelper';

import styles from './postShowStyles.scss';
import mentionStyling from '@buyside/styles/module/form/mentionStyling';

const PostCommentForm: React.FC<{
  canCommentAnonymously: boolean;
  closeForm?: () => void;
  closeFormOpenThread?: () => void;
  feedItemId?: string;
  isAllocator: boolean;
  isChildComment: boolean;
  mentionables: SuggestionDataItem[];
  parentId: string | null;
  postId: string;
  profileImageUrl?: Maybe<string>;
}> = ({
  canCommentAnonymously,
  closeForm,
  closeFormOpenThread,
  feedItemId,
  isAllocator,
  isChildComment,
  mentionables,
  parentId,
  postId,
  profileImageUrl
}) => {
  const [commentBody, setCommentBody] = useState('');

  const [createComment, { error }] = useCreatePostCommentMutation({
    update(cache, { data }) {
      const newComment = data?.createComment?.comment;

      if (!newComment) return;

      if (feedItemId) {
        const existingCommentData = cache.readQuery<CommentsQuery, CommentsQueryVariables>({
          query: FeedItemCommentsQuery,
          variables: { feedItemId }
        });

        if (existingCommentData) {
          cache.writeQuery({
            query: FeedItemCommentsQuery,
            data: {
              feedItemComments: {
                edges: [
                  { node: newComment },
                  ...(existingCommentData?.feedItemComments?.edges || [])
                ],
                pageInfo: existingCommentData?.feedItemComments?.pageInfo
              },
              feedItemShow: existingCommentData.feedItemShow
            }
          });
        }

        cache.modify({
          id: cache.identify({ id: postId, __typename: 'Post' }),
          fields: {
            commentsCount: (cachedCount): number => cachedCount + 1
          }
        });
      } else {
        const cachedQuery = cache.readQuery<PostCommentsQuery>({
          query: PostCommentsDocument,
          variables: { postId }
        });
        if (!cachedQuery?.post?.comments) return;

        cache.writeQuery<PostCommentsQuery>({
          query: PostCommentsDocument,
          data: {
            ...cachedQuery,
            post: {
              ...cachedQuery.post,
              comments: {
                edges: [{ node: newComment }, ...(cachedQuery.post.comments.edges || [])],
                pageInfo: cachedQuery.post.comments.pageInfo
              },
              commentsCount: (cachedQuery.post.commentsCount || 0) + 1
            }
          },
          variables: { postId }
        });
      }
    }
  });

  const {
    formState: { errors, isDirty, isSubmitting },
    handleSubmit,
    register,
    setValue
  } = useForm<CreatePostCommentMutationVariables>();

  const onSubmit: SubmitHandler<CreatePostCommentMutationVariables> = ({
    anonymous
  }): Promise<void> =>
    createComment({ variables: { postId, body: commentBody, anonymous, parentId } }).then(() => {
      if (isChildComment && closeFormOpenThread) {
        closeFormOpenThread();
        return;
      }
      setCommentBody('');
      // the reset method of useForm disables the input completely after submit - TMA (8 Jul 2021)
      setValue('body', '', { shouldDirty: false });
      setValue('anonymous', false, { shouldDirty: false });
    });

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={classnames({ [styles.postCommentForm]: isChildComment })}
    >
      <div className={styles.commentAuthorWrap}>
        <ProfileImage
          source={profileImageUrl || defaultUserImageUrl()}
          size={IMAGE_SIZES.SMALLER}
        />
        <MentionsInput
          id="body"
          style={mentionStyling}
          disabled={isSubmitting}
          {...register('body')}
          name="body"
          onChange={(e): void => {
            setCommentBody(e.target.value);
            setValue('body', e.target.value, { shouldDirty: true });
          }}
          placeholder="Post your comment"
          value={commentBody}
        >
          <Mention
            data={mentionables}
            appendSpaceOnAdd
            style={{ backgroundColor: '#DEEDF5' }}
            trigger="@"
          />
        </MentionsInput>
      </div>
      {errors.body && <div className={styles.error}>{errors.body.message}</div>}
      {mentionables && (
        <p className={classnames(styles.note, styles.italicLight)}>
          Note: Type '@' to tag participants in this comment thread. Tagged members will be
          notified.
        </p>
      )}
      {isAllocator ? (
        <p className={styles.note}>*As an allocator, all of your comments are anonymous.</p>
      ) : (
        <>
          {canCommentAnonymously && isDirty && (
            <div className={styles.anonCheckbox}>
              <label>
                <input id="anonymous" {...register('anonymous')} type="checkbox" />
                Post this comment anonymously
              </label>
            </div>
          )}
        </>
      )}
      <Button
        isLoading={isSubmitting}
        disabled={!isDirty}
        text="Post"
        type={BUTTON_TYPES.PRIMARY}
      />
      {isChildComment && (
        <Button
          isLoading={isSubmitting}
          text="Cancel"
          type={BUTTON_TYPES.SECONDARY}
          onClick={closeForm}
        />
      )}
      {error && <div className={styles.error}>{error.graphQLErrors[0].message}</div>}
    </form>
  );
};

export default PostCommentForm;
